/* eslint-disable no-undef */
import { requireNotNull } from '../util/utils';
import { LOCAL_PORT } from '../constants';
import Logger from '../util/Logger';

let pusher = null;

const stringType = typeof ('string');

const init = () => {
  if (!pusher) {
    try {
      Logger.info('Initialize pusher');

      const DEV_KEY = 'a803ee7cce7e6b433b4a';
      const STG_KEY = 'b75c4f6d6eb0d3da9c92';
      const APP_KEY = '3a67f3c949d5497a3cae';

      let key;
      let authEndpoint;
      const hostSlice = window.location.hostname.slice(0, 3);
      switch (hostSlice) {
        case 'loc': // local
          authEndpoint = `http://${window.location.hostname}${LOCAL_PORT}/popin/api/1.0/pusher/auth`;
          key = DEV_KEY;
          Pusher.logToConsole = true;
          break;
        // FALLTHROUGH
        case 'dev': // dev
        case 'tes': // test1
        case 'bet': // beta
          authEndpoint = `https://${window.location.hostname}/popin/api/1.0/pusher/auth`;
          key = DEV_KEY;
          Pusher.logToConsole = true;
          break;
        case 'dem': // demo
          authEndpoint = `https://${window.location.hostname}/popin/api/1.0/pusher/auth`;
          key = DEV_KEY;
          Pusher.logToConsole = false;
          break;
        case 'stg': // stg
          authEndpoint = `https://${window.location.hostname}/popin/api/1.0/pusher/auth`;
          key = STG_KEY;
          Pusher.logToConsole = true;
          break;
        case 'pre': // preview
        case 'app': // preview
          authEndpoint = `https://${window.location.hostname}/popin/api/1.0/pusher/auth`;
          key = APP_KEY;
          Pusher.logToConsole = false;
          break;
        default:
          Logger.error('Unknown environment :', window.location.hostname);
          key = null;
          authEndpoint = null;
      }

      if (!!authEndpoint && !!key) {
        pusher = new Pusher(key, {
          authEndpoint,
          cluster: 'us2',
          forceTLS: true
        });
      }
    } catch (e) {
      Logger.error('Coult not initialize Pusher', e);
    }
  }
};

/**
 * Class which abstracts the unbinding behavior of connections
 *
 * Outside classes should not construct new Unbinder instances.
 *
 * If created with a valid pusher channel, an eventName and callback are expected.
 * If the channel is valid, it will be unbound - else this interface is a no-op.
 */
class Unbinder {

  constructor (channel, eventName, callback) {
    this.channel = channel;
    this.eventName = eventName;
    this.callback = callback;

    // If this is a valid unbinder, it must all be valid
    // But if its a no-op unbinder, we don't care
    if (!!channel) {
      requireNotNull(eventName);
      requireNotNull(callback);
    }
  }

  unbind () {
    if (!!this.channel && !!this.callback && !!this.eventName) {
      this.channel.unbind(this.eventName, this.callback);

      // Once we unbind the channel, burn it
      this.channel = null;
      this.eventName = null;
      this.callback = null;
    }
  }
}

/**
 * Collections have an events field, events_count, and a short_code field.
 * Events don't.
 *
 * @param object
 * @return {boolean}
 */
const isObjectCollection = (object) => {
  return !!object.events && !!object.events_count && !!object.short_code;
};

/**
 * User has a display_name field, events and collections dont
 * Events don't.
 *
 * @param object
 * @return {boolean}
 */
const isObjectUser = (object) => {
  return !!object.display_name;
};

export class Realtime {

  /**
   * Subscribe to a new realtime event channel via the Event object
   *
   * You must provide an onUpdate and onError callback
   *
   * Calling this function multiple times will bind to the event multiple times.
   * If you wish to avoid duplicate callback events, you must use the returned Unbinder
   * interface to unsub yourself before calling subscribe again
   *
   * TODO: This is an ugly function because the API was originally designed at the event
   *       level to just take a generic Event instead of an Event or a Collection.
   *       The server was not namespacing channels so this was ok. Now the server namespaces
   *       channels - so we have to carry the legacy baggage.
   *
   * @param objectWithId
   * @param eventName
   * @param onUpdate
   * @param onError
   */
  static subscribeToRealtime (objectWithId, eventName, onUpdate, onError) {
    // Developer
    requireNotNull(objectWithId);
    requireNotNull(eventName);
    requireNotNull(onUpdate);
    requireNotNull(onError);

    if (!pusher) {
      init();
    }

    // Open pusher
    if (pusher) {
      let channelName;
      if (isObjectCollection(objectWithId)) {
        channelName = `private-collection-${objectWithId.id}`;
      } else if (isObjectUser(objectWithId)) {
        channelName = `private-user-${objectWithId.id}`;
      } else {
        channelName = `private-event-${objectWithId.id}`;
      }
      const channel = pusher.subscribe(channelName);
      try {
        const callback = data => {
          let parsed = null;
          if (typeof(data) === stringType) {
            if (data === 'Logout') {
              parsed = {};
            } else {
              try {
                parsed = JSON.parse(data);
              } catch (e) {
                Logger.error('Something went wrong when receiving realtime', e);
                onError(e);
              }
            }
          } else {
            parsed = data;
          }

          if (!!parsed) {
            onUpdate(parsed);
          }
        };
        channel.bind(eventName, callback);

        return new Unbinder(channel, eventName, callback);
      } catch (e) {
        Logger.error('Something went wrong with pusher', e);
        onError(e);
      }
    }

    return new Unbinder(null, null, null);
  }

  /**
   * Kill all realtime connections
   *
   * Pusher does this automatically, you should not normally need to call this yourself.
   */
  static unsubscribeRealtime (event) {
    if (pusher) {
      pusher.unsubscribe(event.id);
    }
  }
}
